import {Nullable, Optional} from '../../Common/TypeHelper'
import {PaymentMethod} from '../DTO/PaymentMethodDTO'
import React from 'react'
import {LabelAndValue} from '../View/Components/PaymentsRelations/InputContentBlocks/MBMemberRadioListForContractTerm'
import {formatPrice} from '../../Common/Formatter'
import {PaymentInformation} from '../View/Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {MBMemberError} from '../Common/MBMemberError'
import {MBContentNoteTypography} from '../View/Atoms/Typographies'

export type NewCreditCardInputInfo = {
  cardNumber: string
  expireMonth: string
  expireYear: string
  holderName: string
  securityCode: string
}

export type SecurityCodeForMyCreditCard = {
  cardId: string
  securityCode: string
}

export type MyCreditCardInputInfo = {
  selectedCardId: string
  securityCodeMap: SecurityCodeForMyCreditCard[]
}

export type CreditCardType = 'newCard' | 'myCard' | ''

export type CreditCardInfo = {
  creditCardType: CreditCardType
  newCreditCardInfo: NewCreditCardInputInfo
  myCreditCardInfo: MyCreditCardInputInfo
}

export type MyCreditCardInfo = {
  cardId: string
  cardNumber: string
  expireDate: string
}

export type UsingMyCreditCardInfo = MyCreditCardInfo & {
  isInUse: boolean
}

export type MobileCarrierKey = 'docomo' | 'softbank' | 'au' | ''

export type MobileCarrierInfo = {
  carrierKey: MobileCarrierKey
}

export type MobileCarrierNextInfo = MobileCarrierInfo & {
  isUnableToCancelContract: boolean
}

export type ConvenienceStoreKey =
  'lawson' |
  'familymart' |
  'ministop' |
  'seicomart' |
  ''

export type ConvenienceStoreInfo = {
  convenienceStoreKey: ConvenienceStoreKey
}

export type CvsContractInfo = {
  convenienceStoreKey: ConvenienceStoreKey
  paymentNumber: Nullable<string>
  paymentNumberLabel: Nullable<string>
  confirmationNumber: Nullable<string>
  confirmationNumberLabel: Nullable<string>
  paymentTerm: Nullable<string>
  paymentDetailUrl: Nullable<string>
  isPaymentAvailableTerm: boolean
  paymentAvailableOn: Nullable<string>
}

export type PaymentInputDetail = {
  creditCard: CreditCardInfo
  mobileCarrier: MobileCarrierInfo
  convenienceStore: ConvenienceStoreInfo
}

export type ContractTerm = 'monthly' | 'yearly' | 'permanent' | ''

export type ContractMethod = 'credit_card' | 'mobile_carrier' | 'convenience_store' | 'none'

export type ContractMethodInputInfo =
  CreditCardInputInfo
  | MobileCarrierInputInfo
  | ConvenienceStoreInputInfo
type CreditCardInputInfo = {
  method: 'credit_card'
  info: CreditCardInfo
}
type MobileCarrierInputInfo = {
  method: 'mobile_carrier'
  info: MobileCarrierInfo
}
type ConvenienceStoreInputInfo = {
  method: 'convenience_store'
  info: ConvenienceStoreInfo
}

export type ContractInputInfo = {
  term: ContractTerm
  method: Optional<ContractMethod>
  amount: number
} & PaymentInputDetail

export type ContractDate = {
  contractDate: string
  contractEndDate: string
}

export type ContractInfoWithContractDate = Pick<ContractInputInfo,
  'term' | 'method' | 'amount'> & {
  contractDate: ContractDate
}

export interface MemberRegisterContractInfo {
  contractTerm: ContractTerm
  paymentMethod: ContractMethod
  creditCardExpireDate: string
  tokenList?: string[]
}

export type MemberRegisterConvenienceStoreContractInfo = Pick<MemberRegisterContractInfo,
  'contractTerm' | 'paymentMethod'> & {
  paymentMethodDetail: ConvenienceStoreKey
}

export type MemberPreRegisterContractInfo = Pick<MemberRegisterContractInfo,
  'contractTerm' | 'paymentMethod'> & {
  paymentMethodDetail: MobileCarrierKey
}

export type MemberRegisterFreeBucketContractInfo = Pick<MemberRegisterContractInfo,
  'paymentMethod' | 'contractTerm'>

export type PaymentInputDetailWithMethod = {
  method: Optional<ContractMethod>
} & PaymentInputDetail

export type ContractInputInfoAll = {
  term: ContractTerm
  amount: number
} & PaymentInputDetailWithMethod

export type ErrorMessagesBaseForContractInput = {
  invalidCardErrors: string[]
  paymentInputTermErrors: string[]
  paymentInputMethodErrors: string[]
  creditCardTypeErrors: string[]
  creditCardNumberErrors: string[]
  creditCardExpireMonthErrors: string[]
  creditCardExpireYearErrors: string[]
  creditCardFutureExpireDateErrors: string[]
  creditCardHolderNameErrors: string[]
  creditCardSecurityCodeErrors: string[]
  errorMyCreditCardId: string,
  myCreditCardSecurityCodeErrors: string[]
  convenienceStoreErrors: string[]
  mobileCarrierErrors: string[]
}

export type ErrorMessagesBaseForContractInputExcludePaymentTerm =
  Omit<ErrorMessagesBaseForContractInput, 'paymentInputTermErrors'>

export type ErrorMessagesForPaymentMethodInput =
  Omit<ErrorMessagesBaseForContractInputExcludePaymentTerm, 'paymentInputMethodErrors'>

export type ErrorMessagesForSignupContract = Omit<ErrorMessagesBaseForContractInput,
  'creditCardTypeErrors' |
  'errorMyCreditCardId' |
  'myCreditCardSecurityCodeErrors'>

export type ErrorMessagesForNewCreditCardInput = Pick<ErrorMessagesForPaymentMethodInput,
  'invalidCardErrors' |
  'creditCardNumberErrors' |
  'creditCardExpireMonthErrors' |
  'creditCardExpireYearErrors' |
  'creditCardFutureExpireDateErrors' |
  'creditCardHolderNameErrors' |
  'creditCardSecurityCodeErrors'>

export type ErrorMessagesForContractEdit = ErrorMessagesBaseForContractInput & {
  isInputError: boolean
}

export const findAmountByTermAndMethod = (
  paymentMethodList: Optional<PaymentMethod[]>, term: Optional<string>, method: Optional<string>,
): number => {

  if (!paymentMethodList) {
    return 0
  }
  const foundPaymentMethod = paymentMethodList.find(
    item => item.method === method && item.term === term,
  )

  return foundPaymentMethod?.amount || 0
}

export const DisplayCreditCardExpireDate = (props: { expireDate: string }): JSX.Element => {
  if (props.expireDate === '') return <></>

  return (
    <MBContentNoteTypography>
      {makeStringCreditCardExpireDate(props.expireDate)}
    </MBContentNoteTypography>
  )
}

export const makeStringCreditCardExpireDate = (expireDate: string): string => {
  const expireMonth = expireDate.split('/')[0]
  const expireYear = expireDate.split('/')[1]

  if (!expireMonth || !expireYear) return ''

  return `（有効期限：${Number(expireMonth)}月/${expireYear}年）`
}

export const getDefaultSecurityCodeMapForMyCreditCard = (
  myCreditCardInfoList: UsingMyCreditCardInfo[],
): SecurityCodeForMyCreditCard[] => {
  if (!myCreditCardInfoList) {
    return []
  }

  return myCreditCardInfoList.map((myCreditCardInfo) => {
    return {cardId: myCreditCardInfo.cardId, securityCode: ''}
  })
}

export const findSecurityCodeByCardIdForValidation = (
  myCreditCardInputInfo: MyCreditCardInputInfo,
): Optional<string> => {

  return myCreditCardInputInfo.securityCodeMap
    .find(item => item.cardId === myCreditCardInputInfo.selectedCardId)?.securityCode
}

export const generateContractTermLabelAndValueList =
  (
    paymentInformationList: PaymentInformation[],
  ): LabelAndValue[] => {
    if (paymentInformationList.length === 0) {
      throw new MBMemberError('SystemError')
    }

    const sortedPaymentInformationList =
      [...paymentInformationList].sort((a, b) => a.order - b.order)

    const emptyArray: LabelAndValue[] = []
    return sortedPaymentInformationList
      .map(item => {
        return {
          label: `${item.termName}：${formatPrice(item.contractAmount)}円(税込)`,
          value: item.term,
        }
      })
      .reduce((accumulator, current) => {
        const alreadyExist =
          accumulator.some(item => (item.label === current.label && item.value === current.value))

        if (!alreadyExist) {
          accumulator.push(current)
        }
        return accumulator
      }, emptyArray)
  }

export const displayCardNumber = (
  selectedMyCardId: string,
  myCreditCardInfoList?: UsingMyCreditCardInfo[],
  tokenResponseCardNumber?: string,
): string => {
  if (tokenResponseCardNumber) {
    return tokenResponseCardNumber
  }

  const selectedMyCardInfo = myCreditCardInfoList?.find(item => item.cardId === selectedMyCardId)
  if (!selectedMyCardInfo) {
    throw new MBMemberError('SystemError')
  }
  return selectedMyCardInfo.cardNumber
}

export const findSelectedCardExpireDate = (
  cardType: CreditCardType,
  newCardExpireDate: string,
  selectedMyCardId: string,
  myCreditCardInfoList?: UsingMyCreditCardInfo[],
): string => {
  if (cardType === 'newCard') {
    return newCardExpireDate
  }

  const selectedMyCardInfo = myCreditCardInfoList?.find(item => item.cardId === selectedMyCardId)
  if (!selectedMyCardInfo) {
    throw new MBMemberError('SystemError')
  }
  return selectedMyCardInfo.expireDate
}

export const ErrorMessageForInvalidCard = (
  cardType: CreditCardType,
  isInvalidCardError?: boolean,
): string[] => {
  if (isInvalidCardError) {
    if (cardType === 'newCard') {
      return ['入力いただいたクレジットカードは利用できません。']
    }

    return ['セキュリティーコードが誤っているか、選択いただいたクレジットカードは利用できません。']
  }

  return []
}

export const getContractMethodsByTerm = (
  paymentList: PaymentMethod[],
  term: ContractTerm,
): ContractMethod[] => Array.from(new Set(term === ''
  ? paymentList.map(payment => payment.method)
  : paymentList.filter(payment => payment.term === term).map(payment => payment.method),
))

export const isFreeBucket = (paymentMethodList: Optional<PaymentMethod[]>): boolean => {
  if (!paymentMethodList) {
    return false
  }
  return paymentMethodList.some(payMethod => payMethod.method === 'none')
}
